import $ from "jquery";
import { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import DeviceStatus from "../NewAeps/DeviceStatus";
import AadhaarPayForm from "./AadhaarPayForm";

const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    const infoVal = (name) =>
        $xml.find(`Info[name='${name}']`).attr("value") ||
        $xml.find(`Param[name='${name}']`).attr("value") ||
        "0";

    return {
        dc:          get("DeviceInfo", "dc"),
        ci:          get("Skey", "ci"),
        hmac:        $xml.find("Hmac").text() || "0",
        dpId:        get("DeviceInfo", "dpId"),
        mc:          get("DeviceInfo", "mc"),
        pidDataType: get("Data", "type"),
        sessionKey:  $xml.find("Skey").text() || "0",
        mi:          get("DeviceInfo", "mi"),
        rdsId:       get("DeviceInfo", "rdsId"),
        errCode:     get("Resp", "errCode"),
        errInfo:     get("Resp", "errInfo"),
        fCount:      get("Resp", "fCount"),
        fType:       get("Resp", "fType"),
        iCount:      get("Resp", "iCount"),
        iType:       get("Resp", "iType"),
        pCount:      get("Resp", "pCount"),
        pType:       "0",
        srno:        infoVal("srno"),
        sysid:       infoVal("sysid"),
        ts:          infoVal("ts"),
        pidData:     $xml.find("Data").text() || "0",
        qScore:      get("Resp", "qScore"),
        nmPoints:    get("Resp", "nmPoints"),
        rdsVer:      get("DeviceInfo", "rdsVer"),
    };
};

const POPUP_COLORS = {
    success: "text-green-600",
    failed:  "text-red-600",
    pending: "text-yellow-600",
};

const AadhaarPayIntegration = () => {
    const { device, captureFingerprint, loading } = useRDService();

    const merchantdetails = useSelector((state) => state.auth?.employeeDetails);
    const { lat, lng }    = useSelector((state) => state.location);

    // Use username (same as AEPS pattern)
    const mid = merchantdetails?.username;

    // ── APIs (Aadhaar Pay specific) ──
    const { callApi: aadhaarPayApi } = useApi(
        "/aeps/fingpay/aadhaar-pay",
        "POST"
    );
    const { callApi: statusCheckApi } = useApi(
        "/aeps/fingpay/aadhaar-pay/status",
        "GET"
    );

    const [processing, setProcessing]             = useState(false);
    const [previousData, setPreviousData]         = useState(null);
    const [clearFormTrigger, setClearFormTrigger] = useState(0);
    const [pendingTxnId, setPendingTxnId]         = useState(null);
    const [statusChecking, setStatusChecking]     = useState(false);

    const [popup, setPopup] = useState({
        show:    false,
        title:   "",
        message: "",
        type:    "",
    });

    const closePopup = () => {
        if (popup.type !== "pending") {
            setClearFormTrigger((prev) => prev + 1);
        }
        setPopup({ show: false, title: "", message: "", type: "" });
    };

    const showPopup = (title, message, type) =>
        setPopup({ show: true, title, message, type });

    // ── Main transaction handler ──
    const handleCapture = async (formData) => {
        try {
            setProcessing(true);
            setPreviousData({ ...formData });
            setPendingTxnId(null);

            const pidXml = await captureFingerprint("");

            if (!pidXml || !pidXml.includes('errCode="0"')) {
                setProcessing(false);
                showPopup("Failed", "Fingerprint Capture Failed", "failed");
                return;
            }

            const pidData = parsePidXml(pidXml);

            const requestBody = {
                mid,
                aadhaar:       formData.aadhaar,
                bankiin:       formData.bank?.iin,
                mobile:        formData.mobile,
                amount:        formData.amount,
                latitude:      lat?.toString(),
                longitude:     lng?.toString(),
                selectedDevice: device || "MANTRA",
                biometricData: pidData,
            };

            let response = null;

            try {
                response = await aadhaarPayApi(requestBody);
            } catch (apiErr) {
                setProcessing(false);
                setPendingTxnId(apiErr?.response?.data?.txnId || null);
                showPopup(
                    "Transaction Pending",
                    `Transaction is under verification. Use "Check Status" if you have a Transaction ID.`,
                    "pending"
                );
                return;
            }

            setProcessing(false);

            if (response?.statuscode === "TXN" || (response?.status === "SUCCESS" && response?.bank_rrn)) {
                showPopup(
                    "Aadhaar Pay Successful",
                    `Amount       : ₹${response?.amount || formData.amount}

Transaction ID : ${response?.txnId || response?.data?.txnId}
Bank RRN       : ${response?.bank_rrn || response?.data?.bankRRN}

${response?.reason || response?.data?.statusMessage || "Request Completed"}`,
                    "success"
                );

            } else if (response?.statuscode === "PEND" || response?.status === "PENDING" || !response) {
                setPendingTxnId(response?.txnId || null);
                showPopup(
                    "Transaction Pending",
                    `Transaction ID : ${response?.txnId || "N/A"}

Transaction is under verification. Use "Check Status" to get the latest update.`,
                    "pending"
                );

            } else {
                showPopup(
                    "Transaction Failed",
                    response?.reason || response?.status || response?.message || "Transaction Failed",
                    "failed"
                );
            }

        } catch (err) {
            setProcessing(false);
            showPopup("Error", err?.message || "Transaction Failed", "failed");
        }
    };

    // ── Status check for pending txns (uses useApi pattern) ──
    const checkStatus = async () => {
        if (!pendingTxnId) return;
        try {
            setStatusChecking(true);

            const res = await statusCheckApi(null, `txnId=${pendingTxnId}`);
            const data = res?.data?.[0] || res?.data;
            const statusCode = data?.transactionStatusCode || data?.statusCode;

            if ((statusCode === "00" || statusCode === "TXN") && (data?.bankRRN || data?.bank_rrn)) {
                setPendingTxnId(null);
                setClearFormTrigger((prev) => prev + 1);
                showPopup(
                    "Aadhaar Pay Successful",
                    `Amount         : ₹${data?.transactionAmount || data?.amount}

Transaction ID : ${data?.merchantTranId || data?.txnId}
Bank RRN       : ${data?.bankRRN || data?.bank_rrn}
Bank           : ${data?.bankName || ""}

${data?.transactionStatusMessage || "Success"}`,
                    "success"
                );

            } else if (statusCode === "FP009" || statusCode === "PEND") {
                showPopup(
                    "Still Pending",
                    `Transaction ID : ${pendingTxnId}

Response is still pending. Please try again after a few seconds.`,
                    "pending"
                );

            } else {
                setPendingTxnId(null);
                setClearFormTrigger((prev) => prev + 1);
                showPopup(
                    "Transaction Failed",
                    data?.transactionStatusMessage || data?.message || "Transaction Failed",
                    "failed"
                );
            }

        } catch (err) {
            showPopup(
                "Status Check Failed",
                err?.message || "Could not fetch transaction status",
                "failed"
            );
        } finally {
            setStatusChecking(false);
        }
    };

    return (
        <div>
            <DeviceStatus device={device} />

            <AadhaarPayForm
                onSubmit={handleCapture}
                processing={processing}
                loading={loading}
                previousData={previousData}
                clearFormTrigger={clearFormTrigger}
            />

            {popup.show && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 w-[420px] text-center">

                        <h2 className={`text-2xl font-semibold ${POPUP_COLORS[popup.type] || "text-gray-800"}`}>
                            {popup.title}
                        </h2>

                        <p className="mt-5 whitespace-pre-line text-[18px] font-bold text-gray-700 leading-8">
                            {popup.message}
                        </p>

                        {popup.type === "pending" && pendingTxnId && (
                            <button
                                onClick={checkStatus}
                                disabled={statusChecking}
                                className="mt-4 w-full bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white py-3 rounded-xl font-semibold"
                            >
                                {statusChecking ? "Checking..." : "Check Status"}
                            </button>
                        )}

                        <button
                            onClick={closePopup}
                            className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold"
                        >
                            {popup.type === "pending" ? "Close" : "OK"}
                        </button>

                    </div>
                </div>
            )}
        </div>
    );
};

export default AadhaarPayIntegration;
